import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Grid, Typography, IconButton, Divider, Stack } from '@mui/material';
import { Facebook, Twitter, LinkedIn, Instagram, YouTube } from '@mui/icons-material';
import { BRAND } from '@bomatrust/shared';

const footerLinks = {
  company: [
    { label: 'About Us', path: '/about' },
    { label: 'How It Works', path: '/how-it-works' },
    { label: 'Contact', path: '/contact' },
    { label: 'Careers', path: '/careers' },
  ],
  properties: [
    { label: 'Browse Properties', path: '/properties' },
    { label: 'Featured Listings', path: '/properties?featured=true' },
    { label: 'Nairobi', path: '/properties?location=nairobi' },
    { label: 'Mombasa', path: '/properties?location=mombasa' },
  ],
  support: [
    { label: 'FAQ', path: '/faq' },
    { label: 'Help Center', path: '/help' },
    { label: 'Terms of Service', path: '/terms' },
    { label: 'Privacy Policy', path: '/privacy' },
  ],
};

const socialLinks = [
  { icon: Facebook, href: `https://facebook.com/${BRAND.social.facebook}`, label: 'Facebook' },
  {
    icon: Twitter,
    href: `https://twitter.com/${BRAND.social.twitter.replace('@', '')}`,
    label: 'Twitter',
  },
  {
    icon: LinkedIn,
    href: `https://linkedin.com/company/${BRAND.social.linkedin}`,
    label: 'LinkedIn',
  },
  { icon: Instagram, href: `https://instagram.com/${BRAND.social.instagram}`, label: 'Instagram' },
  { icon: YouTube, href: 'https://youtube.com', label: 'YouTube' },
];

/**
 * Footer - Site footer with links and social icons
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      role="contentinfo"
      aria-label="Site footer"
      sx={{
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        pt: 6,
        pb: 3,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {BRAND.name}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                opacity: 0.9,
                mb: 1,
                fontStyle: 'italic',
                color: 'secondary.main',
              }}
            >
              Made for the Diaspora
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 2, maxWidth: 280 }}>
              {BRAND.description}
            </Typography>

            {/* Social Icons */}
            <Stack direction="row" spacing={1}>
              {socialLinks.map(social => (
                <IconButton
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  sx={{
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  <social.icon />
                </IconButton>
              ))}
            </Stack>
          </Grid>

          {/* Company Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Company
            </Typography>
            <Stack spacing={1}>
              {footerLinks.company.map(link => (
                <Link key={link.path} to={link.path} style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.contrastText',
                      opacity: 0.8,
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    {link.label}
                  </Typography>
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Properties Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Properties
            </Typography>
            <Stack spacing={1}>
              {footerLinks.properties.map(link => (
                <Link key={link.path} to={link.path} style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.contrastText',
                      opacity: 0.8,
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    {link.label}
                  </Typography>
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Support Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Support
            </Typography>
            <Stack spacing={1}>
              {footerLinks.support.map(link => (
                <Link key={link.path} to={link.path} style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.contrastText',
                      opacity: 0.8,
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    {link.label}
                  </Typography>
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Newsletter */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Stay Updated
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Subscribe to our newsletter for the latest listings and investment tips.
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />

        {/* Bottom Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {BRAND.copyright(currentYear)}
          </Typography>

          <Stack direction="row" spacing={3}>
            <Link to="/terms" style={{ textDecoration: 'none' }}>
              <Typography
                variant="body2"
                sx={{ color: 'primary.contrastText', opacity: 0.7, '&:hover': { opacity: 1 } }}
              >
                Terms
              </Typography>
            </Link>
            <Link to="/privacy" style={{ textDecoration: 'none' }}>
              <Typography
                variant="body2"
                sx={{ color: 'primary.contrastText', opacity: 0.7, '&:hover': { opacity: 1 } }}
              >
                Privacy
              </Typography>
            </Link>
            <Link to="/cookies" style={{ textDecoration: 'none' }}>
              <Typography
                variant="body2"
                sx={{ color: 'primary.contrastText', opacity: 0.7, '&:hover': { opacity: 1 } }}
              >
                Cookies
              </Typography>
            </Link>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
